import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';

import { v4 as uuidv4 } from 'uuid';
import SelectFilter from '../../../Common/UI/Filter/SelectFilter';
import MultiSelectFilter from '../../../Common/UI/Filter/MultiSelectFilter';
import NumberFilter from '../../../Common/UI/Filter/NumberFilter';
import DateFilter from '../../../Common/UI/Filter/DateFilter';
import StringFilter from '../../../Common/UI/Filter/StringFilter';
import AreaFilter from '../../../Common/UI/Filter/AreaFilter';
import { GroupPropType } from '../../../../utility/prop_types/CriteriaPropType';

const propTypes = {
    systemName: PropTypes.string.isRequired,
    fields: PropTypes.object.isRequired,
    filters: GroupPropType.isRequired,
    onSetFilters: PropTypes.func.isRequired
};

const useStyles = makeStyles((theme) => styles(theme));

function LayerFilters ({ systemName, fields, filters, onSetFilters }) {
    const classes = useStyles();

    function handleChange (branch) {
        const newTree = { ...filters };
        const idx = newTree.children.findIndex(el => el.id === branch.id);
        if (idx === -1) {
            newTree.children.push(branch);
        } else {
            newTree.children[idx] = branch;
        }
        onSetFilters(systemName, newTree);
    }

    function handleClear (id) {
        const idx = filters.children.findIndex(el => el.id === id);
        // Date fields are a special case because of their internal state, on change is only called when a valid date comes through
        // While on clear is always called whenever the internal state changes just in case, meaning the idx may not exist, so this safely prevents any errors
        if (idx === -1) {
            return;
        }
        const newTree = { ...filters };
        const newChildren = [...newTree.children];
        newChildren.splice(idx, 1);
        newTree.children = newChildren;
        onSetFilters(systemName, newTree);
    }

    function sort (firstEl, secondEl) {
        const order1 = fields[firstEl].order || 0;
        const order2 = fields[secondEl].order || 0;
        if (order1 < order2) return -1;
        if (order1 > order2) return 1;
        return 0;
    }

    function getFilter (key) {
        const field = fields[key];
        const branch = filters.children.find(el => el.field === key) || {
            id: uuidv4(),
            type: 'rule',
            field: key,
            properties: {}
        };
        

        switch (field.type) {
            case 'select':
                return <SelectFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'multiselect':
                return <MultiSelectFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'integer':
                return <NumberFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'date':
                return <DateFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'string':
                return <StringFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'area':
                return <AreaFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            default:
                return <Fragment key={branch.id} />;
        }
    }
    return (
        <div className={classes.root}>
            {
                Object.keys(fields)
                    .filter(key => !fields[key].tags || fields[key].tags.includes('tile'))
                    .sort(sort)
                    .map(getFilter)
            }
        </div>
    );
}

const styles = theme => ({
    root: {
        '&> *:not(:last-child)':{
            marginBottom: theme.constants.verticalMargin
        }
    },
    sliderContainer: {
        background: theme.palette.background.main,
        margin: '5px 0',
        border: `1px solid ${theme.palette.background.border}`,
        borderRadius: '4px',
        padding: '10px 0 5px 0'
    },
    sliderInput: {
        padding: '0 20px'
    },
    sliderTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 10px'
    },
    margin: {
        margin: theme.spacing(1, 0, 1, 0)
    },
    select: {
        '& .MuiSelect-icon': {
            color: theme.palette.text.primary
        }
    },
    textField: {
        ...theme.typography.subtitle1,
        background: theme.palette.background.main,
        borderRadius: '4px'
    }
});

LayerFilters.propTypes = propTypes;

export default LayerFilters;